import { useCallback, useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import api from '../api/client'
import Boton from '../components/Boton'
import Modal from '../components/Modal'
import { colors, font, text, space, radius, sx } from '../design/tokens'
import { fmtFechaHora } from '../utils/fecha'

/**
 * Seguridad — segundo factor (TOTP) del propio usuario. Martin, 18 sep 2026.
 *
 *  - Apagado: "Activar" muestra el QR (y el secreto en texto por si no puede
 *    escanear) + el código de la app. Al confirmar, los 8 códigos de rescate
 *    UNA sola vez.
 *  - Prendido: fecha de activación, regenerar códigos (pide código),
 *    dispositivos confiables con "Quitar", y "Desactivar" (contraseña +
 *    código; bloqueado si el rol lo exige).
 *
 * `bloqueada`: el rol exige el 2FA y no está activado — la app entera muestra
 * SOLO esta pantalla (gate en App.jsx) hasta que lo active.
 */
export default function SeguridadPage({ bloqueada = false }) {
  const { actualizarUsuario, logout, realUser } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()

  const [estado, setEstado] = useState(null)
  const [cargando, setCargando] = useState(true)
  const [error, setError] = useState('')
  const [aviso, setAviso] = useState(location.state?.aviso || '')

  // Activación: 'qr' (escaneando) → 'codigos' (mostrando los de rescate)
  const [paso, setPaso] = useState(null)
  const [setup, setSetup] = useState(null)
  const [codigo, setCodigo] = useState('')
  const [confirmando, setConfirmando] = useState(false)
  const [codigosRescate, setCodigosRescate] = useState(null)
  const [copiado, setCopiado] = useState(false)

  // Modales de la sección "prendido"
  const [modal, setModal] = useState(null) // 'regenerar' | 'desactivar'

  // `cargando` arranca en true y solo baja: en las recargas posteriores el
  // estado anterior sigue en pantalla hasta que llega el nuevo.
  const cargar = useCallback(() => api.get('/totp/estado')
    .then(({ data }) => setEstado(data))
    .catch(() => setError('No se pudo leer el estado del segundo factor.'))
    .finally(() => setCargando(false)), [])

  useEffect(() => { cargar() }, [cargar])

  // Refresca los flags del usuario real (tfa_habilitado / tfa_obligatorio):
  // es lo que levanta el gate de App.jsx cuando activa.
  const refrescarMe = async () => {
    try {
      const { data } = await api.get('/me')
      actualizarUsuario(data)
    } catch { /* el próximo montaje lo pide de nuevo */ }
  }

  const activar = async () => {
    setError('')
    try {
      const { data } = await api.post('/totp/generar')
      setSetup(data)
      setCodigo('')
      setPaso('qr')
    } catch (err) {
      setError(err.response?.data?.message || 'No se pudo generar el código QR.')
    }
  }

  const confirmar = async (e) => {
    e.preventDefault()
    setError('')
    setConfirmando(true)
    try {
      const { data } = await api.post('/totp/confirmar', { codigo })
      setCodigosRescate(data.codigos_rescate)
      setCopiado(false)
      setPaso('codigos')
      await refrescarMe()
      await cargar()
    } catch (err) {
      const errs = err.response?.data?.errors
      setError(errs ? Object.values(errs).flat().join(' ') : (err.response?.data?.message || 'No se pudo confirmar.'))
      setCodigo('')
    } finally {
      setConfirmando(false)
    }
  }

  const copiarCodigos = async () => {
    try {
      await navigator.clipboard.writeText(codigosRescate.join('\n'))
      setCopiado(true)
    } catch {
      setError('No se pudo copiar: seleccioná los códigos y copialos a mano.')
    }
  }

  const terminarCodigos = () => {
    setPaso(null)
    setCodigosRescate(null)
    if (bloqueada) navigate('/dashboard', { replace: true })
  }

  const quitarDispositivo = async (d) => {
    if (!window.confirm(`Quitar "${d.nombre || 'este dispositivo'}"? La próxima vez que entre desde ahí va a pedir el código.`)) return
    try {
      await api.delete(`/totp/dispositivos/${d.id}`)
      await cargar()
    } catch (err) {
      setError(err.response?.data?.message || 'No se pudo quitar el dispositivo.')
    }
  }

  const cerrarSesion = async () => {
    await logout()
    navigate('/login', { replace: true })
  }

  const contenido = (
    <div style={st.page}>
      {bloqueada && (
        <div style={st.bannerBloqueo}>
          <strong>Tu rol requiere el segundo factor: activalo para seguir.</strong>
          <span>Necesitás una app de autenticación en el celular (Google Authenticator, Authy o 1Password). Lleva un minuto.</span>
        </div>
      )}

      {aviso && (
        <div style={st.bannerAviso}>
          <span>{aviso}</span>
          <button type="button" style={st.cerrarAviso} onClick={() => setAviso('')} aria-label="Cerrar">×</button>
        </div>
      )}

      <div style={st.header}>
        <div>
          <h1 style={st.h1}>Seguridad</h1>
          <p style={st.sub}>Segundo factor de autenticación (código de 6 dígitos de una app, además de la contraseña).</p>
        </div>
        {bloqueada && <Boton variante="ghost" onClick={cerrarSesion}>Cerrar sesión</Boton>}
      </div>

      {error && <div style={st.error}>{error}</div>}

      {cargando && !estado && <p style={st.sub}>Cargando…</p>}

      {estado && paso === null && !estado.habilitado && (
        <div style={sx.card}>
          <h2 style={sx.cardTitle}>Estado</h2>
          <p style={st.estadoLinea}>
            <span style={st.chipOff}>Desactivado</span>
            {estado.obligatorio && <span style={st.obligatorio}>Obligatorio para tu rol</span>}
          </p>
          <p style={st.texto}>
            Al activarlo, cada vez que entres vas a poner tu contraseña y después el código que muestra la app. Si perdés el celular, entrás con un código de rescate.
          </p>
          <Boton variante="primary" onClick={activar}>Activar</Boton>
        </div>
      )}

      {paso === 'qr' && setup && (
        <div style={sx.card}>
          <h2 style={sx.cardTitle}>Paso 1 · Escaneá el código</h2>
          <p style={st.texto}>Abrí tu app de autenticación, elegí “Agregar cuenta” y escaneá este código.</p>
          <div style={st.qrRow}>
            <div style={st.qr} dangerouslySetInnerHTML={{ __html: setup.qr_svg }} />
            <div style={st.secretoBox}>
              <div style={st.secretoLabel}>Si no podés escanear, cargá esta clave a mano</div>
              <code style={st.secreto}>{setup.secret.replace(/(.{4})/g, '$1 ').trim()}</code>
              <div style={st.secretoMeta}>Tipo: basado en tiempo · Cuenta: {realUser?.email}</div>
            </div>
          </div>

          <h2 style={{ ...sx.cardTitle, marginTop: space['2xl'] }}>Paso 2 · Confirmá con el código</h2>
          <form onSubmit={confirmar} style={st.formCodigo}>
            <input
              style={st.inputCodigo}
              value={codigo}
              onChange={e => setCodigo(e.target.value.replace(/\D/g, '').slice(0, 6))}
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="123456"
              maxLength={6}
              autoFocus
            />
            <Boton variante="primary" type="submit" disabled={confirmando || codigo.length !== 6}>
              {confirmando ? 'Verificando…' : 'Confirmar y activar'}
            </Boton>
            <Boton variante="ghost" type="button" onClick={() => { setPaso(null); setSetup(null); setError('') }}>Cancelar</Boton>
          </form>
        </div>
      )}

      {paso === 'codigos' && codigosRescate && (
        <div style={sx.card}>
          <h2 style={sx.cardTitle}>Listo · Códigos de rescate</h2>
          <p style={st.texto}>
            El segundo factor quedó activado. Estos 8 códigos sirven para entrar si perdés el celular. Cada uno se usa una sola vez.
          </p>
          <div style={st.avisoFuerte}>Guardalos ahora en un lugar seguro: no se vuelven a mostrar.</div>
          <div style={st.codigosGrid}>
            {codigosRescate.map(c => <code key={c} style={st.codigoRescate}>{c}</code>)}
          </div>
          <div style={st.acciones}>
            <Boton variante="secondary" onClick={copiarCodigos}>{copiado ? 'Copiados' : 'Copiar'}</Boton>
            <Boton variante="primary" onClick={terminarCodigos}>Ya los guardé</Boton>
          </div>
        </div>
      )}

      {estado && paso === null && estado.habilitado && (
        <>
          <div style={sx.card}>
            <h2 style={sx.cardTitle}>Estado</h2>
            <p style={st.estadoLinea}>
              <span style={st.chipOn}>Activado</span>
              {estado.activado_at && <span style={st.meta}>desde el <span style={sx.mono}>{fmtFechaHora(estado.activado_at)}</span></span>}
              {estado.obligatorio && <span style={st.obligatorio}>Obligatorio para tu rol</span>}
            </p>
            <p style={st.texto}>
              Códigos de rescate disponibles: <strong style={sx.mono}>{estado.codigos_rescate_restantes}</strong> de 8.
              {estado.codigos_rescate_restantes <= 2 && ' Quedan pocos: regeneralos.'}
            </p>
            <div style={st.acciones}>
              <Boton variante="secondary" onClick={() => { setError(''); setModal('regenerar') }}>Regenerar códigos de rescate</Boton>
              <Boton
                variante="destructive"
                disabled={estado.obligatorio}
                title={estado.obligatorio ? 'Tu rol requiere el segundo factor: si lo perdiste, pedile el reseteo a un administrador.' : undefined}
                onClick={() => { setError(''); setModal('desactivar') }}
              >
                Desactivar
              </Boton>
            </div>
            {estado.obligatorio && (
              <p style={st.hint}>No se puede desactivar: tu rol lo requiere. Si perdiste el celular y los códigos, otro administrador lo resetea desde Usuarios.</p>
            )}
          </div>

          <div style={{ ...sx.card, marginTop: space.xl }}>
            <h2 style={sx.cardTitle}>Dispositivos confiables</h2>
            <p style={st.texto}>Navegadores donde marcaste “Confiar en este dispositivo por 30 días”: entran sin pedir el código hasta que vencen o los quitás.</p>
            {estado.dispositivos.length === 0 ? (
              <p style={st.hint}>Ninguno.</p>
            ) : (
              <table style={sx.table}>
                <thead>
                  <tr>
                    <th style={sx.th}>Dispositivo</th>
                    <th style={sx.th}>IP</th>
                    <th style={sx.th}>Último uso</th>
                    <th style={sx.th}>Vence</th>
                    <th style={sx.th}></th>
                  </tr>
                </thead>
                <tbody>
                  {estado.dispositivos.map(d => (
                    <tr key={d.id} style={st.fila}>
                      <td style={st.td} title={d.nombre || ''}>{d.nombre || '—'}</td>
                      <td style={{ ...st.td, ...sx.mono }}>{d.ip || '—'}</td>
                      <td style={{ ...st.td, ...sx.mono }}>{fmtFechaHora(d.ultimo_uso_at)}</td>
                      <td style={{ ...st.td, ...sx.mono }}>{fmtFechaHora(d.expira_at)}</td>
                      <td style={{ ...st.td, textAlign: 'right' }}>
                        <Boton variante="secondary" sm onClick={() => quitarDispositivo(d)}>Quitar</Boton>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}

      {modal === 'regenerar' && (
        <ModalConCodigo
          titulo="Regenerar códigos de rescate"
          texto="Los 8 códigos anteriores dejan de servir. Confirmá con el código de tu app."
          accion="Regenerar"
          onClose={() => setModal(null)}
          enviar={async ({ codigo }) => {
            const { data } = await api.post('/totp/codigos-rescate', { codigo })
            setModal(null)
            setCodigosRescate(data.codigos_rescate)
            setCopiado(false)
            setPaso('codigos')
            await cargar()
          }}
        />
      )}

      {modal === 'desactivar' && (
        <ModalConCodigo
          titulo="Desactivar el segundo factor"
          texto="Vas a volver a entrar solo con la contraseña. Confirmá con tu contraseña y el código de la app."
          accion="Desactivar"
          conPassword
          onClose={() => setModal(null)}
          enviar={async ({ password, codigo }) => {
            await api.post('/totp/desactivar', { password, codigo })
            setModal(null)
            await refrescarMe()
            await cargar()
          }}
        />
      )}
    </div>
  )

  if (!bloqueada) return contenido

  return (
    <div style={st.shell}>
      <div style={st.shellCard}>{contenido}</div>
    </div>
  )
}

/** Modal chico: (contraseña +) código de la app o de rescate → acción. */
function ModalConCodigo({ titulo, texto, accion, conPassword = false, onClose, enviar }) {
  const [password, setPassword] = useState('')
  const [codigo, setCodigo] = useState('')
  const [error, setError] = useState('')
  const [enviando, setEnviando] = useState(false)

  const submit = async (e) => {
    e.preventDefault()
    setError('')
    setEnviando(true)
    try {
      await enviar({ password, codigo: codigo.trim() })
    } catch (err) {
      const errs = err.response?.data?.errors
      setError(errs ? Object.values(errs).flat().join(' ') : (err.response?.data?.message || 'No se pudo completar.'))
    } finally {
      setEnviando(false)
    }
  }

  return (
    <Modal title={titulo} onClose={onClose}>
      <form onSubmit={submit} style={st.formModal}>
        <p style={st.texto}>{texto}</p>
        {conPassword && (
          <div>
            <label style={st.label}>Contraseña</label>
            <input style={st.input} type="password" value={password} onChange={e => setPassword(e.target.value)} autoComplete="current-password" autoFocus required />
          </div>
        )}
        <div>
          <label style={st.label}>Código de la app (o un código de rescate)</label>
          <input style={st.input} value={codigo} onChange={e => setCodigo(e.target.value)} autoComplete="one-time-code" placeholder="123456" autoFocus={!conPassword} required />
        </div>
        {error && <div style={st.error}>{error}</div>}
        <div style={st.accionesModal}>
          <Boton variante="ghost" type="button" onClick={onClose}>Cancelar</Boton>
          <Boton variante={accion === 'Desactivar' ? 'destructive' : 'primary'} type="submit" disabled={enviando}>
            {enviando ? 'Enviando…' : accion}
          </Boton>
        </div>
      </form>
    </Modal>
  )
}

const st = {
  page: { maxWidth: 760, display: 'flex', flexDirection: 'column', gap: space.xl },
  shell: { minHeight: '100vh', background: colors.bgSoft, display: 'flex', justifyContent: 'center', padding: `${space['3xl']}px ${space.xl}px`, boxSizing: 'border-box' },
  shellCard: { width: '100%', maxWidth: 760 },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: space.xl },
  h1: { margin: 0, fontSize: text.title, fontWeight: 600, color: colors.text },
  sub: { margin: `${space.xs}px 0 0`, fontSize: text.sec, color: colors.textSec },
  texto: { margin: `${space.md}px 0 ${space.lg}px`, fontSize: text.body, color: colors.text, lineHeight: 1.5 },
  hint: { margin: `${space.md}px 0 0`, fontSize: text.sec, color: colors.textMeta, lineHeight: 1.45 },
  meta: { fontSize: text.sec, color: colors.textSec },
  bannerBloqueo: {
    display: 'flex', flexDirection: 'column', gap: space.xs,
    background: colors.warnBg, border: `1px solid ${colors.warnBorder}`, color: colors.warn,
    borderRadius: radius.md, padding: `${space.lg}px ${space.xl}px`, fontSize: text.body, lineHeight: 1.45,
  },
  bannerAviso: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: space.md,
    background: colors.infoBg, border: `1px solid ${colors.infoBorder}`, color: colors.info,
    borderRadius: radius.md, padding: `${space.md}px ${space.xl}px`, fontSize: text.body,
  },
  cerrarAviso: { background: 'none', border: 'none', color: colors.info, fontSize: 18, cursor: 'pointer', lineHeight: 1, padding: 0 },
  error: {
    fontSize: text.sec, color: colors.err, background: colors.errBg,
    border: `1px solid ${colors.errBorder}`, borderRadius: radius.sm, padding: `${space.sm}px ${space.md}px`,
  },
  estadoLinea: { display: 'flex', alignItems: 'center', gap: space.md, margin: `${space.md}px 0 0`, flexWrap: 'wrap' },
  chipOn: { display: 'inline-block', padding: '2px 8px', borderRadius: radius.xs, fontSize: text.meta, fontWeight: 600, textTransform: 'uppercase', background: colors.okBg, color: colors.ok },
  chipOff: { display: 'inline-block', padding: '2px 8px', borderRadius: radius.xs, fontSize: text.meta, fontWeight: 600, textTransform: 'uppercase', background: colors.bgSoft, color: colors.textSec, border: `1px solid ${colors.border}` },
  obligatorio: { display: 'inline-block', padding: '2px 8px', borderRadius: radius.xs, fontSize: text.meta, fontWeight: 600, textTransform: 'uppercase', background: colors.warnBg, color: colors.warn },
  qrRow: { display: 'flex', gap: space['2xl'], alignItems: 'flex-start', flexWrap: 'wrap' },
  qr: { width: 220, height: 220, flex: '0 0 auto', border: `1px solid ${colors.border}`, borderRadius: radius.md, overflow: 'hidden', background: colors.bg },
  secretoBox: { flex: 1, minWidth: 240, display: 'flex', flexDirection: 'column', gap: space.sm },
  secretoLabel: { fontSize: text.sec, color: colors.textSec },
  secreto: { fontFamily: font.mono, fontSize: text.body, letterSpacing: '0.04em', background: colors.bgSoft, border: `1px solid ${colors.border}`, borderRadius: radius.sm, padding: `${space.md}px ${space.lg}px`, wordBreak: 'break-all', userSelect: 'all' },
  secretoMeta: { fontSize: text.meta, color: colors.textMeta },
  formCodigo: { display: 'flex', gap: space.md, alignItems: 'center', flexWrap: 'wrap', marginTop: space.md },
  inputCodigo: {
    width: 150, padding: `${space.sm}px ${space.md}px`, fontFamily: font.mono, fontSize: 20, letterSpacing: '0.2em',
    textAlign: 'center', border: `1px solid ${colors.border}`, borderRadius: radius.sm, boxSizing: 'border-box',
  },
  avisoFuerte: {
    background: colors.warnBg, border: `1px solid ${colors.warnBorder}`, color: colors.warn,
    borderRadius: radius.sm, padding: `${space.sm}px ${space.md}px`, fontSize: text.body, fontWeight: 600, marginBottom: space.lg,
  },
  codigosGrid: { display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: space.sm, marginBottom: space.lg },
  codigoRescate: { fontFamily: font.mono, fontSize: 15, letterSpacing: '0.06em', background: colors.bgSoft, border: `1px solid ${colors.border}`, borderRadius: radius.sm, padding: `${space.md}px ${space.lg}px`, userSelect: 'all' },
  acciones: { display: 'flex', gap: space.md, flexWrap: 'wrap', marginTop: space.md },
  fila: { borderBottom: `1px solid ${colors.borderSoft}` },
  td: { padding: `${space.md}px ${space.xl}px`, fontSize: text.body, color: colors.text, maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  formModal: { display: 'flex', flexDirection: 'column', gap: space.md },
  label: { display: 'block', fontSize: text.sec, color: colors.textSec, marginBottom: 4 },
  input: { width: '100%', padding: `${space.sm}px ${space.md}px`, fontSize: text.body, border: `1px solid ${colors.border}`, borderRadius: radius.sm, boxSizing: 'border-box' },
  accionesModal: { display: 'flex', gap: space.sm, justifyContent: 'flex-end', marginTop: space.sm },
}
